<template>
  <div class="tw-max-h-full">
    <GraphqlDataTable
      :query
      :additional-query-variables="props.jobId ? { jobs: [props.jobId] } : {}"
      :order-by="initialOrder"
      :filter-black-list="['accounts', 'externalIdentifiers', 'jobs']"
      :column-black-list="props.jobId ? ['HireTitleNavigationPoNumberColumn'] : []"
      :quick-filters="['activeStatus']"
      :search-placeholder="$t('Search name, role, PO number...')"
      :max-height-adjustment="30"
      :bulk-actions="actions"
    >
      <template #action="{ row }">
        <HireActionColumn :row="row" />
      </template>
      <template #full-screen-globals>
        <ModalRemindToBill
          :hire-ids="selectedRowsIds"
          :is-open="openRemindModal"
          bulk-reminder
          :default-message="defaultRemindToBillText"
          @closed="selectedRowsIds = []"
          @reminder-sent="selectedRowsIds = []"
        />
      </template>
    </GraphqlDataTable>
  </div>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsIconNameEnum } from '@worksome/wds-components'
import { computed, ref } from 'vue'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { graphql } from '@/gql'
import { HireOrderByColumn, SortOrder } from '@/gql/graphql'
import HireActionColumn from '@/components/GraphqlDataTable/columns/Hire/HireActionColumn.vue'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { $t } from '@/composables/translation'
import ModalRemindToBill from '@/components/ModalRemindToBill.vue'
import { useAccountSettings } from '@/composables/useAccountSettings'

const props = defineProps<{
  jobId?: string
}>()

const query = graphql(`
  query HireContractsQuery(
    $accounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [HiresOrderByClauseInput!]
    $activeStatus: [HireActiveStatus!]
    $engagementTypeSetup: [EngagementTypeSetup!]
    $usesClassification: [ClassificationUsage!]
    $startDateRange: DateRangeInput
    $endDateRange: DateRangeInput
    $createdAtDateRange: DateRangeInput
    $hireOwners: [ID!]
    $workers: [ID!]
    $externalIdentifiers: [String!]
    $hasPaymentRequests: Boolean
    $hasRecruiterAttribution: Boolean
    $recruiterOwnershipIsExpired: Boolean
    $jobs: [ID!]
    $missingPaymentRequestDateRange: DateRangeInput
  ) {
    hires(
      accounts: $accounts
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      startDateRange: $startDateRange
      endDateRange: $endDateRange
      createdAtDateRange: $createdAtDateRange
      activeStatus: $activeStatus
      engagementTypeSetup: $engagementTypeSetup
      usesClassification: $usesClassification
      workers: $workers
      externalIdentifiers: $externalIdentifiers
      owners: $hireOwners
      hasPaymentRequests: $hasPaymentRequests
      hasRecruiterAttribution: $hasRecruiterAttribution
      recruiterOwnershipIsExpired: $recruiterOwnershipIsExpired
      jobs: $jobs
      missingPaymentRequestDateRange: $missingPaymentRequestDateRange
      includeDeleted: true
    ) {
      data {
        ...HireCreatedColumn
        ...HireAvatarColumn

        ...HireTitleNavigationPoNumberColumn

        startDate
        endDate

        ...HireTenureColumn

        ...RateColumnHire

        ...HireHasStaffingAgencyColumn
        # Payment requests
        ...HirePaymentRequestColumn
        ...HireLastPaymentRequestColumn

        ...HireEngagementColumn

        ...HireClassificationColumn

        ...HireContractPopoverColumn

        ...HireActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const initialOrder = {
  field: HireOrderByColumn.CreatedAt,
  order: SortOrder.Desc,
}

const selectedRowsIds = ref<string[]>([])

const openRemindModal = computed(() => selectedRowsIds.value.length > 0)
const defaultRemindToBillText = computed(() => {
  const setting = useAccountSettings('default_message_remind_freelancer_to_bill').value
  return typeof setting === 'string' ? setting : ''
})

const actions: DataTableBulkAction[] = [
  {
    name: $t('Remind to submit payment request'),
    icon: WdsIconNameEnum.SEND_ALT,
    action: (rows) => {
      selectedRowsIds.value = Object.values(rows).map((row) => (row as any)?.number)
    },
  },
]
</script>
